import { LandingZoneRoleModel } from "@api/types/landingzoneroles";
import { Manager } from "@components/form/state/types";
import React, { useState, useCallback } from "react";
import {
  createStyles,
  Theme,
  withStyles,
  WithStyles,
} from "@material-ui/core/styles";
import MuiDialogTitle from "@material-ui/core/DialogTitle";
import CloseIcon from "@material-ui/icons/Close";
import {
  Box,
  Button,
  IconButton,
  Typography,
  CircularProgress,
  MenuItem,
  Select,
  Dialog,
  DialogContent,
} from "@material-ui/core";
import Form from "@components/form";
import {
  postLandingZoneRole,
  putLandingZoneRole,
} from "@api/admin/landingzoneroles";
import { alertService } from "@services";
import { useLandingZoneRoleStore } from "../../../../../../store/hooksy/landingZoneRoles";

const styles = (theme: Theme) =>
  createStyles({
    root: {
      margin: 0,
      padding: theme.spacing(2),
    },
    closeButton: {
      position: "absolute",
      right: theme.spacing(1),
      top: theme.spacing(1),
      color: theme.palette.grey[500],
    },
  });

interface DialogTitleProps extends WithStyles<typeof styles> {
  id: string;
  children: React.ReactNode;
  onClose: () => void;
}

type Key = keyof LandingZoneRoleModel;

const DialogTitle = withStyles(styles)((props: DialogTitleProps) => {
  const { children, classes, onClose, ...other } = props;
  return (
    <MuiDialogTitle disableTypography className={classes.root} {...other}>
      <Typography variant="h6">{children}</Typography>
      {onClose ? (
        <IconButton
          aria-label="close"
          className={classes.closeButton}
          onClick={onClose}
        >
          <CloseIcon />
        </IconButton>
      ) : null}
    </MuiDialogTitle>
  );
});

type Props = {
  open: boolean;
  onCancel: () => void;
  form: Manager<LandingZoneRoleModel>;
};

export const LandingZoneRoleDetail: React.FC<Props> = ({
  open,
  onCancel,
  form,
}) => {
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const [, setLandingZoneRoleHooksyStore] = useLandingZoneRoleStore();

  const landingZoneRoleModel: LandingZoneRoleModel = {
    id: 0,
    landingZoneRoleName: "",
    isActive: false,
  };

  function clearModelAndClosePopup() {
    landingZoneRoleModel.id = 0;
    landingZoneRoleModel.landingZoneRoleName = "";
    landingZoneRoleModel.isActive = false;
    form.reset();
    onCancel();
  }

  function onChange(name: Key, value: any) {
    form.set(Form.target.VALUE, [name, value]);
    validate([name, value]).then(([name, error]) => {
      form.set(Form.target.ERROR, [name, error]);
    });
  }

  function validate([name, value]: [string, any]): Promise<[Key, Error?]> {
    switch (name) {
      case "landingZoneRoleName":
        return Promise.resolve([name as Key, Form.Validators.hasValue(value)]);
      default:
        return Promise.resolve([name as Key]);
    }
  }

  const handleSubmit = useCallback(() => {
    Promise.all(Object.entries(form.values).map(validate)).then(
      (validations) => {
        const valid = validations.reduce((valid, [name, error]) => {
          return valid && !error;
        }, true);
        if (valid) {
          submitPayload();
        } else {
          validations.forEach(([name, error]) => {
            form.set(Form.target.ERROR, [name, error]);
          });
        }
      }
    );
  }, [form]);

  function submitPayload() {
    landingZoneRoleModel.id = form.values.id;
    landingZoneRoleModel.landingZoneRoleName = form.values.landingZoneRoleName;
    landingZoneRoleModel.isActive = form.values.isActive;
    console.log(landingZoneRoleModel);
    setIsSubmitting(true);
    // update
    if (landingZoneRoleModel.id > 0) {
      putLandingZoneRole(landingZoneRoleModel)
        .then((response) =>
          alertService.success({
            msg: "Landing Zone Role successfully updated!",
          })
        )
        .catch((error) =>
          alertService.danger({ statusCode: error.response.status })
        )
        .finally(() => {
          setIsSubmitting(false);
          clearModelAndClosePopup();
          setLandingZoneRoleHooksyStore({ fetchList: true });
        });
    }
    // create
    else {
      postLandingZoneRole(landingZoneRoleModel)
        .then((response) =>
          alertService.success({
            msg: "Landing Zone Role successfully created!",
          })
        )
        .catch((error) =>
          alertService.danger({ statusCode: error.response.status })
        )
        .finally(() => {
          setIsSubmitting(false);
          clearModelAndClosePopup();
          setLandingZoneRoleHooksyStore({ fetchList: true });
        });
    }
  }

  const modalTitle = form.values.id === 0 ? "Add New" : "Update";
  const submitText = isSubmitting ? "Submitting..." : "Submit";
  return (
    <Box mt={2}>
      <Dialog open={open} fullWidth>
        <DialogTitle
          id="form-dialog-title"
          onClose={() => clearModelAndClosePopup()}
        >
          {`${modalTitle} Landing Zone Role`}
        </DialogTitle>
        <DialogContent>
          <Form>
            <Form.Textbox
              label="Landing Zone Role Name"
              name="landingZoneRoleName"
              onChange={onChange}
              size={12}
              error={form.errors.landingZoneRoleName}
              value={form.values.landingZoneRoleName}
              required
            />
            <Form.Checkbox
              label="Is Active"
              name="isActive"
              onChange={onChange}
              size={12}
              value={form.values.isActive}
            />
          </Form>
          <Box textAlign="right" mt={4}>
            <Button
              variant="outlined"
              color="primary"
              onClick={() => clearModelAndClosePopup()}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              color="primary"
              type="button"
              style={{ marginLeft: "40px" }}
              onClick={handleSubmit}
              disabled={isSubmitting}
            >
              {submitText}
            </Button>
          </Box>
        </DialogContent>
      </Dialog>
    </Box>
  );
};
